// Knowledge base for the AI Portfolio Assistant chatbot
interface KnowledgeEntry {
  keywords: string[]
  response: string
  category: string
}
const knowledgeBase: KnowledgeEntry[] = [
  // About & Background
  {
    keywords: ['who', 'mick', 'about', 'background', 'introduction'],
    response:
      'Mick van Latum is a UX Designer and AI Developer who specializes in translating complex questions into elegant solutions. He combines expertise in UX Design, AI Integration, Development, and Systems Thinking to create innovative, user-centered solutions.',
    category: 'about',
  },
  {
    keywords: ['experience', 'years', 'how long'],
    response:
      'Mick has over 5 years of experience in the field, working on 10+ projects with a 100% client satisfaction rate. His work spans across UX design, AI integration, and full-stack development.',
    category: 'about',
  },
  // Expertise & Skills
  {
    keywords: [
      'skills',
      'expertise',
      'what can',
      'capabilities',
      'technologies',
    ],
    response:
      "Mick's core expertise includes:\n• AI Integration (95% proficiency)\n• React & TypeScript (95%)\n• UX Design (90%)\n• Systems Thinking (92%)\n\nHe specializes in translating complex problems into elegant, accessible solutions by combining design, development, and AI.",
    category: 'expertise',
  },
  {
    keywords: ['ai', 'artificial intelligence', 'machine learning', 'ml'],
    response:
      'Mick has extensive experience with AI integration, including building AI-powered design systems, implementing natural language processing for user research analysis, and creating intelligent assistants. He focuses on making AI accessible and valuable for non-technical users.',
    category: 'expertise',
  },
  {
    keywords: ['react', 'typescript', 'javascript', 'frontend', 'development'],
    response:
      'Mick is highly proficient in React and TypeScript (95% proficiency), with expertise in building scalable, component-based applications. He follows best practices for performance optimization, accessibility, and maintainable code architecture.',
    category: 'expertise',
  },
  {
    keywords: ['ux', 'design', 'user experience', 'interface', 'ui'],
    response:
      'Mick approaches UX design with a focus on accessibility and intuitive interactions. He conducts user research, creates design systems, and ensures every solution is both beautiful and functional. His design philosophy centers on understanding the real problem behind each request.',
    category: 'expertise',
  },
  // Process & Methodology
  {
    keywords: ['process', 'workflow', 'methodology', 'approach', 'how work'],
    response:
      'Mick follows a 5-phase translation process:\n1. Question & Analysis - Understanding the real need\n2. Conceptual Thinking - Exploring solution directions\n3. Design & Prototype - Creating tangible designs\n4. Development & Integration - Building the solution\n5. Iteration & Improvement - Continuous refinement\n\nThis structured yet flexible approach ensures complex problems are translated into effective solutions.',
    category: 'process',
  },
  {
    keywords: ['translate', 'translation', 'complex', 'elegant'],
    response:
      "Translation is at the core of Mick's approach. He translates:\n• Complex technical concepts → User-friendly interfaces\n• Vague requirements → Clear solutions\n• Business needs → Technical implementations\n• Data patterns → Visual insights\n\nThis translation process combines technology, design, and strategic thinking.",
    category: 'process',
  },
  // Projects & Portfolio
  {
    keywords: ['projects', 'portfolio', 'work', 'examples', 'case studies'],
    response:
      'Mick has worked on several notable projects including:\n• AI-Powered Design System (40% faster design-to-code)\n• UX Research Dashboard (65% reduction in analysis time)\n• Interactive Prototype Builder (75% faster prototyping)\n• Conversational AI Design Kit (42% improvement in conversation success)\n\nYou can explore these projects in detail through the Portfolio app!',
    category: 'portfolio',
  },
  {
    keywords: ['design system', 'tokens', 'components'],
    response:
      'Mick developed an AI-powered design system that automatically generates and maintains design tokens based on brand guidelines. It uses machine learning to suggest accessible color combinations and reduced design-to-code time by 40% while improving accessibility scores by 27%.',
    category: 'portfolio',
  },
  // Contact & Collaboration
  {
    keywords: [
      'contact',
      'reach',
      'email',
      'hire',
      'collaborate',
      'work together',
    ],
    response:
      "You can reach Mick at:\n• Email: mick@mvlcreative.com\n• Phone: +31 6 1234 5678\n• Location: Amsterdam, Netherlands\n\nHe's always interested in discussing new projects and collaborations. You can also use the Contact app to send a message directly!",
    category: 'contact',
  },
  {
    keywords: ['available', 'availability', 'freelance', 'hiring'],
    response:
      'Mick is available for interesting projects and collaborations. He specializes in translating complex challenges into elegant solutions. Feel free to reach out through the Contact app to discuss how he can help with your project!',
    category: 'contact',
  },
  // Tools & Technologies
  {
    keywords: ['tools', 'software', 'figma', 'design tools'],
    response:
      'Mick works with a range of modern tools including:\n• Design: Figma, Adobe Creative Suite\n• Development: React, TypeScript, Tailwind CSS\n• AI/ML: Python, TensorFlow, OpenAI APIs\n• Collaboration: Git, Notion, Slack\n\nHe focuses on tools that enable efficient collaboration and high-quality output.',
    category: 'expertise',
  },
  // Philosophy & Approach
  {
    keywords: ['philosophy', 'belief', 'approach', 'values', 'principles'],
    response:
      "Mick's core philosophy is about translation - understanding the real problem behind each request and creating solutions that are both innovative and practical. He believes in:\n• User-centered design\n• Accessible technology\n• Continuous learning\n• Collaborative problem-solving\n• Balancing ambition with feasibility",
    category: 'about',
  },
  // Accessibility
  {
    keywords: ['accessibility', 'a11y', 'wcag', 'inclusive', 'accessible'],
    response:
      "Accessibility is a core principle in Mick's work. He implements WCAG standards, ensures keyboard navigation, provides proper ARIA labels, and designs for various abilities. His Conversational AI Design Kit achieved 100% WCAG 2.1 compliance.",
    category: 'expertise',
  },
  // Default/Fallback
  {
    keywords: ['help', 'what', 'how', 'can you'],
    response:
      "I can help you learn about:\n• Mick's expertise and skills\n• His process and methodology\n• Past projects and case studies\n• How to get in touch\n• Technologies he works with\n\nWhat would you like to know more about?",
    category: 'general',
  },
]
export function findBestResponse(userInput: string): string {
  const normalizedInput = userInput.toLowerCase().trim()
  // Find all matching entries
  const matches = knowledgeBase.filter((entry) =>
    entry.keywords.some((keyword) => normalizedInput.includes(keyword)),
  )
  // If we have matches, return the best one (first match for now)
  if (matches.length > 0) {
    return matches[0].response
  }
  // Fallback response
  return "That's an interesting question! I can tell you about Mick's expertise in UX Design and AI Development, his process for translating complex problems into solutions, his portfolio projects, or how to get in touch. What would you like to know more about?"
}
